import dayjs from 'dayjs';

/**
 * Format a date with the specified format
 * @param {Date|string} date - The date to format
 * @param {string} format - The format string (dayjs format)
 * @returns {string} - The formatted date
 */
export function formatDate(date, format = 'MM/DD/YYYY') {
  if (!date) return '';
  return dayjs(date).format(format);
}

/**
 * Format a date with a short date format
 * @param {Date|string} date - The date to format
 * @returns {string} - The formatted date
 */
export function formatShortDate(date) {
  return formatDate(date, 'MM/DD/YYYY');
}

/**
 * Format a time with a short time format
 * @param {Date|string} date - The date/time to format
 * @returns {string} - The formatted time
 */
export function formatShortTime(date) {
  if (!date) return '';
  return dayjs(date).format('h:mm A');
}

/**
 * Format a date with a short date and time format
 * @param {Date|string} date - The date/time to format
 * @returns {string} - The formatted date and time
 */
export function formatShortDateTime(date) {
  if (!date) return '';
  return dayjs(date).format('MM/DD/YYYY h:mm A');
}

/**
 * Format a date with a long date format
 * @param {Date|string} date - The date to format
 * @returns {string} - The formatted date
 */
export function formatLongDate(date) {
  if (!date) return '';
  return dayjs(date).format('MMMM D, YYYY');
}

/**
 * Format a currency value
 * @param {number} value - The value to format
 * @param {string} currency - The currency code
 * @param {string} locale - The locale
 * @returns {string} - The formatted currency
 */
export function formatCurrency(value, currency = 'USD', locale = 'en-US') {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format a phone number
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - The formatted phone number
 */
export function formatPhone(phoneNumber) {
  if (!phoneNumber) return '';
  
  // Remove all non-digits
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Format US phone number (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
  }
  
  // Format with country code +X (XXX) XXX-XXXX
  if (cleaned.length === 11) {
    return `+${cleaned.substring(0, 1)} (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7, 11)}`;
  }
  
  // Return original if it doesn't match expected formats
  return phoneNumber;
}